import { ComponentProps } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  MessageDescriptor,
} from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '__generated__/globalTypes';
import { RotowireSmallLogo } from 'atoms/icons/RotowireLogo';
import { Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { LabelS } from 'atoms/typography';
import { Cost } from 'components/player/Cost';
import { PoweredByRotowire } from 'components/rotowire/PoweredByRotowire';
import { scoutingCenter } from 'lib/glossary';
import { laptopAndAbove } from 'style/mediaQuery';

const AUTHORIZED_SO5_FIXTURE_EVENTS: So5FixtureEvent[] = [
  So5FixtureEvent.DAILY,
  So5FixtureEvent.CLASSIC,
];
const StyledLabelS = styled(LabelS)`
  @media ${laptopAndAbove} {
    font: var(--t-label-m);
    font-weight: bold;
  }
`;
type Props = {
  size: ComponentProps<typeof Cost>['size'];
  projectedScore: number | null;
  so5FixtureEvent: So5FixtureEvent;
  withTooltip?: boolean;
  withLogo?: boolean;
};

const tooltipMessages: { [k in So5FixtureEvent]?: MessageDescriptor } = {
  [So5FixtureEvent.DAILY]: scoutingCenter.projectionDailyTooltip,
  [So5FixtureEvent.CLASSIC]: scoutingCenter.projectionClassicTooltip,
};

export const ProjectionScore = ({
  size,
  projectedScore,
  so5FixtureEvent,
  withTooltip,
  withLogo,
}: Props) => {
  if (
    !AUTHORIZED_SO5_FIXTURE_EVENTS.includes(so5FixtureEvent) ||
    !projectedScore
  )
    return null;

  const cost = (
    <Cost
      size={size}
      color="var(--c-rotowire)"
      score={
        <Vertical gap={0} center>
          {!!withLogo && <RotowireSmallLogo size={2} />}
          <StyledLabelS bold color="var(--c-white)">
            <FormattedNumber value={projectedScore} maximumFractionDigits={0} />
          </StyledLabelS>
        </Vertical>
      }
      inverted={so5FixtureEvent === So5FixtureEvent.DAILY}
    />
  );
  if (withTooltip) {
    return (
      <Tooltip
        title={
          <LabelS className="text-center">
            <FormattedMessage
              {...tooltipMessages[so5FixtureEvent]}
              values={{ rotowireLogo: <PoweredByRotowire /> }}
            />
          </LabelS>
        }
      >
        {cost}
      </Tooltip>
    );
  }
  return cost;
};
